import { useState } from 'react'
import { supabase } from '../api/supabase'

export interface ConsolParent {
  id: string
  job_ref: string
  job_type: string
  direction: string
  transport_mode: string
}

export interface ConsolChild {
  id: string
  job_ref: string
  file_ref: number
  job_ref_index?: number
  status?: string
  house_bl_no?: string
  master_bl_no?: string
  importer_name?: string
  importer_addr_l1?: string
  importer_addr_l2?: string
  importer_city?: string
  importer_postcode?: string
  importer_country?: string
  consignor_name?: string
  consignor_addr_l1?: string
  consignor_addr_l2?: string
  consignor_city?: string
  consignor_postcode?: string
  consignor_country?: string
  total_packs?: number
  total_gross_weight?: number
  total_charg_weight?: number
  total_volume?: number
  goods_description?: string
  container_numbers?: string
}

export function useConsol() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Fetch child HBL jobs for a master consolidation job
   * @param masterId The ID of the CONSOL master job
   * @returns Array of child HBL jobs
   */
  const fetchConsolChildren = async (masterId: string): Promise<ConsolChild[]> => {
    try {
      setLoading(true)
      setError(null)

      const { data: houseLinks, error: linksError } = await supabase
        .from('job_consol')
        .select('*')
        .eq('master_job_id', masterId)

      if (linksError) throw linksError

      if (!houseLinks || houseLinks.length === 0) {
        return []
      }

      const houseIds = houseLinks.map((h: any) => h.house_job_id)

      // Fetch from view: v_consol_children_summary (optimized, no N+1)
      const { data: children, error: childrenError } = await supabase
        .from('v_consol_children_summary')
        .select('*')
        .in('id', houseIds)
        .order('job_ref_index', { ascending: true })

      if (childrenError) throw childrenError

      return children || []
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch consol children'
      setError(errorMsg)
      console.error('Error fetching consol children:', err)
      return []
    } finally {
      setLoading(false)
    }
  }

  /**
   * Fetch parent MBL job for a house consolidation job
   * @param houseId The ID of the SINGLE house job
   * @returns The parent MBL job or null
   */
  const fetchConsolParent = async (houseId: string): Promise<ConsolParent | null> => {
    try {
      setLoading(true)
      setError(null)

      const { data: masterLink, error: linkError } = await supabase
        .from('job_consol')
        .select('*')
        .eq('house_job_id', houseId)
        .limit(1)
        .single()

      if (linkError && linkError.code !== 'PGRST116') {
        throw linkError
      }

      if (!masterLink) {
        return null
      }

      const { data: masterJob, error: jobError } = await supabase
        .from('jobs')
        .select('id, job_ref, job_type, direction, transport_mode')
        .eq('id', masterLink.master_job_id)
        .single()

      if (jobError) throw jobError

      return masterJob || null
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch consol parent'
      setError(errorMsg)
      console.error('Error fetching consol parent:', err)
      return null
    } finally {
      setLoading(false)
    }
  }

  // Z93: link / unlink / add go through RPCs that keep jobs.parent_job_id + job_ref_index and the
  // job_consol row in step in ONE transaction (the old client-side insert/delete only touched
  // job_consol). They never rename an existing job. Errors come back as readable messages --
  // never swallowed, never a raw Postgres code.
  const rpcCall = async <T,>(fn: string, args: Record<string, unknown>): Promise<{ ok: true; data: T } | { ok: false; error: string }> => {
    setLoading(true)
    setError(null)
    try {
      const { data, error: rpcError } = await supabase.rpc(fn, args)
      if (rpcError) throw rpcError
      return { ok: true, data: data as T }
    } catch (err) {
      const raw = err instanceof Error ? err.message : (err as { message?: string })?.message || 'Unknown error'
      const msg = /permission denied|42501/i.test(raw) ? 'You do not have permission to do this.' : raw
      setError(msg)
      console.error(`Consol RPC ${fn} failed:`, err)
      return { ok: false, error: msg }
    } finally {
      setLoading(false)
    }
  }

  const addConsolChild = (masterId: string, input: { houseBlNo?: string; importerId?: string | null; consignorId?: string | null; containerNo?: string }) =>
    rpcCall<{ id: string; job_ref: string; job_ref_index: number }>('consol_add_child', {
      p_master_id: masterId,
      p_house_bl_no: input.houseBlNo || null,
      p_importer_id: input.importerId || null,
      p_consignor_id: input.consignorId || null,
      p_container_no: input.containerNo || null,
    })

  const linkJobToConsol = (masterId: string, jobId: string) =>
    rpcCall<{ id: string; job_ref: string; job_ref_index: number; boxtrax_synced: boolean }>('consol_link_job', { p_master_id: masterId, p_job_id: jobId })

  const unlinkJobFromConsol = (masterId: string, jobId: string) =>
    rpcCall<{ id: string; job_ref: string; boxtrax_synced: boolean }>('consol_unlink_job', { p_master_id: masterId, p_job_id: jobId })

  return {
    loading,
    error,
    fetchConsolChildren,
    fetchConsolParent,
    addConsolChild,
    linkJobToConsol,
    unlinkJobFromConsol,
  }
}
